import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Plus, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import PageHeader from '@/components/PageHeader';
import { getEmergencyContacts, createEmergencyContact, deleteEmergencyContact } from '@/utils/api';
import { toast } from 'sonner';

export default function Profilo({ patient }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    nome: '',
    relazione: '',
    telefono: '',
    email: '',
  });

  const loadContacts = async () => {
    try {
      const response = await getEmergencyContacts(patient.id);
      setContacts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento contatti:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [patient.id]);

  const handleAddContact = async (e) => {
    e.preventDefault();

    try {
      await createEmergencyContact({
        patient_id: patient.id,
        ...newContact,
        priorita: contacts.length + 1,
      });

      toast.success('Contatto aggiunto con successo!');
      setDialogOpen(false);
      setNewContact({ nome: '', relazione: '', telefono: '', email: '' });
      loadContacts();
    } catch (error) {
      console.error('Errore nell\'aggiunta del contatto:', error);
      toast.error('Errore nell\'aggiunta del contatto');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteEmergencyContact(contactId);
      toast.success('Contatto eliminato');
      loadContacts();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const age = patient.data_nascita
    ? new Date().getFullYear() - new Date(patient.data_nascita).getFullYear()
    : null;

  return (
    <div className="page-container pb-24">
      <PageHeader title="Profilo" subtitle="Informazioni personali e contatti" icon={User} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-8 soft-shadow border border-slate-200/60 mb-6"
      >
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="w-10 h-10 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {patient.nome} {patient.cognome}
            </h2>
            {age && <p className="text-base text-slate-600 mb-4">{age} anni</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600 mb-1">Data di Nascita</p>
                <p className="text-base font-semibold text-slate-900">
                  {new Date(patient.data_nascita).toLocaleDateString('it-IT')}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600 mb-1">ID Paziente</p>
                <p className="text-base font-semibold text-slate-900 truncate">{patient.id.slice(0, 8)}...</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-8 soft-shadow-lg text-white mb-6"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <AlertCircle className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-1">Contatti di Emergenza</h2>
            <p className="text-red-100 text-base">Persone da contattare in caso di necessità</p>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-contact"
              className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-white text-red-600 hover:bg-red-50 shadow-lg transition-all duration-300 active:scale-[0.98]"
            >
              <Plus className="w-6 h-6 mr-2" />
              Aggiungi Contatto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Nuovo Contatto</DialogTitle>
              <DialogDescription className="text-base">
                Aggiungi un contatto di emergenza
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddContact} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="nome" className="text-base font-medium mb-2 block">
                  Nome Completo
                </Label>
                <Input
                  id="nome"
                  data-testid="input-contact-nome"
                  required
                  value={newContact.nome}
                  onChange={(e) => setNewContact({ ...newContact, nome: e.target.value })}
                  className="h-14 text-base rounded-xl"
                  placeholder="es. Mario Rossi"
                />
              </div>
              <div>
                <Label htmlFor="relazione" className="text-base font-medium mb-2 block">
                  Relazione
                </Label>
                <Input
                  id="relazione"
                  data-testid="input-contact-relazione"
                  required
                  value={newContact.relazione}
                  onChange={(e) => setNewContact({ ...newContact, relazione: e.target.value })}
                  className="h-14 text-base rounded-xl"
                  placeholder="es. Figlio, Coniuge"
                />
              </div>
              <div>
                <Label htmlFor="telefono" className="text-base font-medium mb-2 block">
                  Telefono
                </Label>
                <Input
                  id="telefono"
                  data-testid="input-contact-telefono"
                  type="tel"
                  required
                  value={newContact.telefono}
                  onChange={(e) => setNewContact({ ...newContact, telefono: e.target.value })}
                  className="h-14 text-base rounded-xl"
                  placeholder="+39 123 456 7890"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-base font-medium mb-2 block">
                  Email (Opzionale)
                </Label>
                <Input
                  id="email"
                  data-testid="input-contact-email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="h-14 text-base rounded-xl"
                  placeholder="email@esempio.it"
                />
              </div>
              <Button
                type="submit"
                data-testid="btn-save-contact"
                className="w-full min-h-[56px] text-base font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white"
              >
                Salva Contatto
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : contacts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-12 soft-shadow border border-slate-200/60 text-center"
          >
            <Phone className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Nessun contatto</h3>
            <p className="text-slate-600">Aggiungi il primo contatto di emergenza</p>
          </motion.div>
        ) : (
          contacts.map((contact, idx) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-white rounded-3xl p-6 soft-shadow border border-slate-200/60"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{contact.nome}</h3>
                    <p className="text-sm text-slate-600 mb-3">{contact.relazione}</p>
                    <div className="space-y-1">
                      <a
                        href={`tel:${contact.telefono}`}
                        className="block text-base font-semibold text-blue-600 hover:underline"
                      >
                        {contact.telefono}
                      </a>
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="block text-sm text-slate-600 hover:underline"
                        >
                          {contact.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid={`btn-delete-contact-${idx}`}
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-3xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-xl font-bold">Conferma eliminazione</AlertDialogTitle>
                      <AlertDialogDescription className="text-base">
                        Sei sicuro di voler eliminare questo contatto? Questa azione non può essere annullata.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteContact(contact.id)}
                        className="bg-red-600 hover:bg-red-700 rounded-xl"
                      >
                        Elimina
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
